import React from 'react';

/**
 * TeacherFeedbackPage component for the HPC report
 * Displays teacher's feedback, performance levels, and observational notes
 *
 * @param {Object} assignment - The assignment data object containing feedback
 * @param {Array} proficiencyMarks - Array of proficiency level markers
 * @param {String} observationalNotes - Teacher's observations
 * @param {Number} pageNumber - Page number for the report
 */
const TeacherFeedbackPage = ({ assignment, proficiencyMarks, observationalNotes }) => {
    // Ensure we have data to display
    if (!assignment && !proficiencyMarks && !observationalNotes) {
        return <div className="report-page">Loading Teacher Feedback...</div>;
    }

    // If proficiencyMarks isn't passed directly, try to get it from assignment
    const marks = proficiencyMarks || (assignment ? assignment.proficiency_marks || [] : []);

    // If observationalNotes isn't passed directly, try to get it from assignment
    const notes = observationalNotes ||
        (assignment ? assignment.observational_notes || assignment.teacher_feedback || '' : '');

    // Get challenge data if available
    const challenges = assignment?.learner_reflection || {};

    // Helper function to check if a level is marked for an ability
    const isLevelMarked = (ability, level) => {
        return marks.some(mark =>
            (mark.criterion === ability || mark.ability === ability) &&
            (mark.level === level || mark.level === level.toLowerCase())
        );
    };

    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            minHeight: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.4',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden'
        }}>


            {/* Teacher's Feedback Header */}
            {/* Teacher's Feedback Header */}
            <div className="bg-orange-600 text-white py-3 px-4 text-center font-bold text-lg">
                Teacher's Feedback
            </div>

            {/* Performance Levels Table */}
            <div className="mb-4">
                <table className="w-full border-collapse border border-orange-400">
                    <thead>
                        <tr>
                            <th className="border border-orange-400 p-2 bg-orange-200 w-1/4 text-left">Abilities</th>
                            <th colSpan="3" className="border border-orange-400 p-2 bg-orange-200 text-center">Key
                                Performance Level Descriptors
                            </th>
                        </tr>
                        <tr>
                            <th className="border border-orange-400 p-2 bg-orange-200"></th>
                            <th className="border border-orange-400 p-2 bg-orange-200 text-center w-1/4">Beginner</th>
                            <th className="border border-orange-400 p-2 bg-orange-200 text-center w-1/4">Proficient</th>
                            <th className="border border-orange-400 p-2 bg-orange-200 text-center w-1/4">Advanced</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Awareness Row */}
                        <tr>
                            <td className="border border-orange-400 p-2 bg-orange-100">Awareness</td>
                            <td className="border border-orange-400 p-2 text-center">
                                {isLevelMarked('Awareness', 'Beginner') && (
                                    <span className="text-lg">✓</span>
                                )}
                            </td>
                            <td className="border border-orange-400 p-2 text-center">
                                {isLevelMarked('Awareness', 'Proficient') && (
                                    <span className="text-lg">✓</span>
                                )}
                            </td>
                            <td className="border border-orange-400 p-2 text-center">
                                {isLevelMarked('Awareness', 'Advanced') && (
                                    <span className="text-lg">✓</span>
                                )}
                            </td>
                        </tr>

                        {/* Sensitivity Row */}
                        <tr>
                            <td className="border border-orange-400 p-2 bg-orange-100">Sensitivity</td>
                            <td className="border border-orange-400 p-2 text-center">
                                {isLevelMarked('Sensitivity', 'Beginner') && (
                                    <span className="text-lg">✓</span>
                                )}
                            </td>
                            <td className="border border-orange-400 p-2 text-center">
                                {isLevelMarked('Sensitivity', 'Proficient') && (
                                    <span className="text-lg">✓</span>
                                )}
                            </td>
                            <td className="border border-orange-400 p-2 text-center">
                                {isLevelMarked('Sensitivity', 'Advanced') && (
                                    <span className="text-lg">✓</span>
                                )}
                            </td>
                        </tr>

                        {/* Creativity Row */}
                        <tr>
                            <td className="border border-orange-400 p-2 bg-orange-100">Creativity</td>
                            <td className="border border-orange-400 p-2 text-center">
                                {isLevelMarked('Creativity', 'Beginner') && (
                                    <span className="text-lg">✓</span>
                                )}
                            </td>
                            <td className="border border-orange-400 p-2 text-center">
                                {isLevelMarked('Creativity', 'Proficient') && (
                                    <span className="text-lg">✓</span>
                                )}
                            </td>
                            <td className="border border-orange-400 p-2 text-center">
                                {isLevelMarked('Creativity', 'Advanced') && (
                                    <span className="text-lg">✓</span>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <p className="text-xs italic mt-1">*Please put a tick mark (✓) to indicate the performance level of each
                    ability.</p>
            </div>

            {/* Observational Notes Section */}
            <div className="mb-4">
                <div className="bg-orange-600 text-white py-2 px-4 font-bold"
                    style={{ backgroundColor: '#EA580C' }}>
                    Observational Notes
                </div>
                <div className="border border-orange-400 p-3 min-h-[280px]">
                    {notes}
                </div>
            </div>

            {/* Think about how the learner performed Section */}
            <div>
                <div className="bg-orange-600 text-white py-2 px-4 font-bold"
                    style={{ backgroundColor: '#EA580C' }}>
                    Think about how the learner performed...
                </div>
                <table className="w-full border-collapse border border-orange-400">
                    <tbody>
                        <tr>
                            <td className="border border-orange-400 p-3 w-1/2">
                                <p className="font-medium">What challenges did the learner face?</p>
                                <div className="min-h-[220px]">
                                    {challenges?.challenge_faced || ''}
                                </div>
                            </td>
                            <td className="border border-orange-400 p-3 w-1/2">
                                <p className="font-medium">How did they overcome them? / How did you help them?</p>
                                <div className="min-h-[220px]">
                                    {challenges?.overcoming_strategy || ''}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TeacherFeedbackPage;